import React from 'react'
import DashboardTable from '../DashboardTable.component'
import { productionData } from '../DashboardMockData'
import { computeIncome, formatNumber, formatValueByUnit } from '@utils/index'
import { DashboardUnits } from '@dataTypes/dashboard.types'

export type Data = {
  [key: string]: number | string
}

export const roleTemplate = (datum: Data): React.ReactElement => (
  <div>
    <i
      className={`bi bi-circle-fill me-1 ${
        datum.roleId === 'GD-001'
          ? 'circle-icon-fill'
          : 'circle-icon-fill-light'
      }`}
    ></i>
    {datum.role}
  </div>
)

export const simpleColTemplate = (
  title: string,
  value: number | string,
  unit: DashboardUnits,
): React.ReactElement => (
  <>
    <div>{title}</div>
    <div>{formatValueByUnit(value, unit)}</div>
  </>
)

function Production(): React.ReactElement {
  const totalProduction = (field: string): number => {
    return productionData.reduce(
      (acc: number, item: any) => acc + item[field],
      0,
    )
  }

  const prodColTemplate = (datum: Data): React.ReactElement => {
    const income = computeIncome(datum.production, datum.prevProd)
    const isIncomeDown = income < 1
    return (
      <>
        <div className="mb-2">Production</div>
        <div className="fs-1 mb-2">{formatNumber(datum.production, true)}</div>
        <div>
          <i
            className={`bi ${
              isIncomeDown
                ? 'bi-arrow-down-circle-fill'
                : 'bi-arrow-up-circle-fill'
            } me-1`}
          ></i>
          {`${isIncomeDown ? 'Down' : 'Up'} ${Math.abs(income).toFixed(0)}% from the previous week`}
        </div>
      </>
    )
  }

  const schedColTemplate = (datum: Data): React.ReactElement => {
    return (
      <>
        <div>Scheduled</div>
        <div>{formatNumber(datum.scheduledActual, true)}</div>
        <div>Goal</div>
        <div>{formatNumber(datum.scheduledGoal, true)}</div>
        <div>
          <a href={'#'} className="me-1">
            <i className="bi bi-pencil-square"></i>
          </a>
          <a href={'#'} className="me-1">
            <i className="bi bi-clock"></i>
          </a>
        </div>
      </>
    )
  }

  const goalColTemplate = (datum: Data): React.ReactElement => {
    return (
      <>
        <div>% of Goal Scheduled</div>
        <div>{`${formatNumber(datum.goalScheduled, false)}%`}</div>
        <div>% of Goal Complete</div>
        <div>{`${formatNumber(datum.goalComplete, false)}%`}</div>
      </>
    )
  }

  const columns = [
    {
      field: 'role',
      header: 'Provider Totals',
      template: roleTemplate,
      width: '100',
    },
    {
      field: 'production',
      header: 'Production',
      template: prodColTemplate,
      headerTemplate: () =>
        simpleColTemplate('Production', totalProduction('production'), '$'),
      width: '200',
    },
    {
      field: 'scheduledActual',
      header: 'Scheduled',
      template: schedColTemplate,
      headerTemplate: () =>
        simpleColTemplate('Schedule', totalProduction('scheduledActual'), '$'),
      width: '100',
    },
    {
      field: 'scheduledGoal',
      header: 'Goal',
      template: goalColTemplate,
      headerTemplate: () =>
        simpleColTemplate('Goal', totalProduction('goalComplete'), '%'),
      width: '150',
    },
  ]

  return (
    <div>
      <DashboardTable
        title={'Production'}
        columns={columns}
        data={productionData}
        allowMultiSorting={false}
        allowSorting={false}
      />
    </div>
  )
}

export default Production
